import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { MaintenanceReportFilterModel } from 'src/app/models/maintenance/maintenance-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { ConstructionSiteService } from 'src/app/services/archive/construction-site.service';
import { CostCenterService } from 'src/app/services/archive/cost-center.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MaintenanceReportService } from 'src/app/services/maintenance/maintenance-report.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-programming-days',
  templateUrl: './programming-days.component.html',
  styleUrl: './programming-days.component.scss',
  standalone: false
})
export class ProgrammingDaysComponent {

  private readonly articleService = inject(ArticleService);
  private readonly branchService = inject(BranchService);
  private readonly constructionSiteService = inject(ConstructionSiteService);
  private readonly costCenterService = inject(CostCenterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly maintenanceReportService = inject(MaintenanceReportService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;
  branchId: number = 0;
  companyId: number = 0;
  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  filteredEnterprise: any[] = [];
  formMaintenanceReportFilter: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listBranch: any[] = [];
  listConstructionSite: any[] = [];
  listCostCenter: any[] = [];
  listState: any[] = [];
  nameWindow: string = 'Informe de Programación por Días';
  projectName = ProjectName.projectName;

  async ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getFirstBranchByUserAuthenticatedAsync();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  async changeBranch(branchId: number | null) {
    if (branchId != 0 && branchId != null) {
      this.branchId = branchId;
      this.formMaintenanceReportFilter.controls["constructionSiteId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["costCenterId"].patchValue(0);
    } else {
      this.formMaintenanceReportFilter.controls["branchId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["constructionSiteId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["costCenterId"].patchValue(0);
    }
    this.changeFilter();
    await this.getAllLists('constructionSites', 'costCenters');
  }

  async changeConstructionSite(constructionSiteId: number | null) {
    if (constructionSiteId != 0 && constructionSiteId != null) {
      this.formMaintenanceReportFilter.controls["costCenterId"].patchValue(0);
    } else {
      this.formMaintenanceReportFilter.controls["constructionSiteId"].patchValue(0);
      this.formMaintenanceReportFilter.controls["costCenterId"].patchValue(0);
    }
    this.changeFilter();
    await this.getAllLists('costCenters');
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
    await this.getFirstBranchByUserAuthenticatedAsync();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  async exportExcel() {
    if (this.changeFilters) {
      const modelProgrammingDaysReportFilter = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
      try {
        const response = await firstValueFrom(
          this.maintenanceReportService.exportMaintenanceReportProgrammingDays(modelProgrammingDaysReportFilter)
        );
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.nameWindow}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists(...listsToLoad: Array<'articles' | 'branches' | 'costCenters' | 'constructionSites'>) {
    try {
      const lists: Record<string, () => Promise<void>> = {
        articles: async () => {
          this.listArticle = await firstValueFrom(
            this.articleService.getAllArticlesByCompanyForSelectAsync(this.companyId)
          )
        },
        branches: async () => {
          this.listBranch = await firstValueFrom(
            this.branchService.getAllBranchesForSelectAsync()
          )
        },
        constructionSites: async () => {
          const currentBranchId = this.formMaintenanceReportFilter.get('branchId')?.value || 0;
          this.listConstructionSite = await firstValueFrom(
            this.constructionSiteService.getAllConstructionSitesForSelect(currentBranchId)
          )
        },
        costCenters: async () => {
          const currentConstructionSiteId = this.formMaintenanceReportFilter.get('constructionSiteId')?.value || 0;
          this.listCostCenter = await firstValueFrom(
            this.costCenterService.getAllCostCentersForSelectByClientAsync(0, currentConstructionSiteId)
          )
        },
        states: async () => {
          this.listState = [
            { value: 0, label: 'Todos' },
            { value: 1, label: 'Programado' },
            { value: 2, label: 'Atrasado' },
            { value: 3, label: 'Óptimo' }
          ];
        }
      };
      let tasks: Promise<void>[];
      if (listsToLoad.length > 0) {
        tasks = listsToLoad.map(key => lists[key]());
      } else {
        tasks = [
          lists.articles(),
          lists.branches(),
          lists.constructionSites(),
          lists.costCenters(),
          lists.states()
        ];
      }
      await Promise.all(tasks);
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formMaintenanceReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.branchId = branch.branchId;
      this.companyId = branch.companyId;
      await this.getAllLists();
    } catch (error) {
      console.error("Error al consultar la sucursal.", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formMaintenanceReportFilter.valid) {
      this.showValidationMessages();
      return;
    }
    try {
      const model = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
      const response = await firstValueFrom(
        this.maintenanceReportService.getMaintenanceReportProgrammingDays(model)
      );
      if (response && response.data?.length > 0) {
        this.dataReport = { ...response };
        this.changeFilters = true;
      } else {
        this.changeFilters = false;
        this.toastHandlerService.showToast(
          'info',
          'No se encontraron datos.'
        );
      }
    } catch (error) {
      this.changeFilters = false;
      console.error("Error", error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      constructionSiteId: {
        notNumber: 'Debe seleccionar una Obra válida.'
      },
      costCenterId: {
        notNumber: 'Debe seleccionar un C.Costos válido.'
      },
      stateId: {
        notNumber: 'Debe seleccionar un Estado válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formMaintenanceReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formMaintenanceReportFilter = this.formBuilder.group({
      branchId: [0],
      articleId: [0, [CustomValidators.numberOnlyValidator()]],
      constructionSiteId: [0, [CustomValidators.numberOnlyValidator()]],
      costCenterId: [0, [CustomValidators.numberOnlyValidator()]],
      stateId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

  validateFormControls() {
    Object.keys(this.formMaintenanceReportFilter.controls).forEach((key) => {
      const control = this.formMaintenanceReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

}
